import React, { useState, useEffect } from 'react'
import Layout from '../component/layout'
import { useNavigate } from 'react-router-dom'
import { toastify } from '../../../utils/toast'
import { makeRequest2 } from '../../../utils/makeRequest'
import { addVasVendor, getValueAddedServices, getVasVendors, updateVasVendor } from '../../../utils/urls'
import { getItem } from '../../../utils/localStorage'

const VASvendors = () => {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('add-vendor')
    const [loading, setLoading] = useState(false)
    const [services, setServices] = useState([])
    const [fetchingServices, setFetchingServices] = useState(false)
    const [serviceVendors, setServiceVendors] = useState([])
    const [fetchingVendors, setFetchingVendors] = useState(false)

    // Modal states
    const [viewModal, setViewModal] = useState({ show: false, vendor: null })
    const [editModal, setEditModal] = useState({ show: false, vendor: null, loading: false })

    // Form states
    const [name, setName] = useState('')
    const [address, setAddress] = useState('')
    const [offers, setOffers] = useState([{ serviceId: '' }])
    const [contactName, setContactName] = useState('')
    const [contactPhone, setContactPhone] = useState('')
    const [contactEmail, setContactEmail] = useState('')
    const [notes, setNotes] = useState('')

    const handleTabClick = (tabId) => {
        setActiveTab(tabId)
        if (tabId === 'view-vendors') {
            fetchServiceVendors()
        } else if (tabId === 'add-vendor') {
            fetchServices()
        }
    }

    // Fetch services for dropdown
    const fetchServices = async () => {
        setFetchingServices(true)
        try {
            const facilityId = await getItem('selectedFacilityId')
            const response = await makeRequest2(`${getValueAddedServices}/${facilityId}`, 'GET')

            if (response.success) {
                const servicesData = response.data?.services || []
                setServices(servicesData)
                console.log('Services set:', servicesData)
            } else {
                toastify('Failed to fetch services', 'error')
            }
        } catch (error) {
            toastify(error.message, 'error')
        } finally {
            setFetchingServices(false)
        }
    }

    const fetchServiceVendors = async () => {
        setFetchingVendors(true)
        try {
            const facilityId = await getItem('selectedFacilityId')
            const response = await makeRequest2(`${getVasVendors}/${facilityId}`, 'GET')

            if (response.success) {
                const vendors = response.data?.vendors || []
                setServiceVendors(vendors)
            } else {
                setServiceVendors([])
                toastify(response.error, 'error')
            }
        } catch (error) {
            console.error('Error fetching service vendors:', error)
            setServiceVendors([])
            toastify(error.message, 'error')
        } finally {
            setFetchingVendors(false)
        }
    }

    const handleAddOffer = () => {
        setOffers([...offers, { serviceId: '' }])
    }

    const handleRemoveOffer = (index) => {
        const updatedOffers = offers.filter((_, i) => i !== index)
        setOffers(updatedOffers)
    }

    const handleOfferChange = (index, field, value) => {
        const updatedOffers = [...offers]
        updatedOffers[index][field] = value
        setOffers(updatedOffers)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        // Validation
        if (!name.trim()) {
            toastify('Vendor name is required', 'error')
            return
        }

        if (!contactName.trim()) {
            toastify('Contact person name is required', 'error')
            return
        }

        if (!contactPhone.trim()) {
            toastify('Contact phone is required', 'error')
            return
        }

        // Validate at least one offer
        const validOffers = offers.filter(offer => offer.serviceId)
        if (validOffers.length === 0) {
            toastify('At least one service offer is required', 'error')
            return
        }

        setLoading(true)

        try {
            const facilityId = await getItem('selectedFacilityId')
            const vendorData = {
                facilityId,
                name,
                address,
                offers: validOffers.map(offer => ({
                    serviceId: offer.serviceId,
                })),
                contactDetails: {
                    name: contactName,
                    phone: contactPhone,
                    email: contactEmail || undefined
                },
                notes: notes || undefined
            }

            const response = await makeRequest2(`${addVasVendor}/${facilityId}`, 'POST', vendorData)

            if (response.success) {
                toastify('VAS Vendor added successfully', 'success')
                // Reset form
                setName('')
                setAddress('')
                setOffers([{ serviceId: ''}])
                setContactName('')
                setContactPhone('')
                setContactEmail('')
                setNotes('')
                // Switch to view vendors tab
                setActiveTab('view-vendors')
                fetchServiceVendors()
            } else {
                throw new Error(response.error)
            }
        } catch (error) {
            toastify(error.message, 'error')
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target

        switch (name) {
            case 'name':
                setName(value)
                break
            case 'address':
                setAddress(value)
                break
            case 'contactName':
                setContactName(value)
                break
            case 'contactPhone':
                setContactPhone(value)
                break
            case 'contactEmail':
                setContactEmail(value)
                break
            case 'notes':
                setNotes(value)
                break
            default:
                break
        }
    }

    // View Modal Handlers
    const handleViewVendor = (vendor) => {
        setViewModal({ show: true, vendor })
    }

    const closeViewModal = () => {
        setViewModal({ show: false, vendor: null })
    }

    // Edit Modal Handlers
    const handleEditVendor = (vendor) => {
        setEditModal({ show: true, vendor, loading: false })
    }

    const closeEditModal = () => {
        setEditModal({ show: false, vendor: null, loading: false })
    }

    const handleEditSubmit = async (e) => {
        e.preventDefault()

        setEditModal(prev => ({ ...prev, loading: true }))

        try {
            const facilityId = await getItem('selectedFacilityId')
            const vendorData = {
                name: editModal.vendor.name,
                address: editModal.vendor.address,
                offers: editModal.vendor.offers,
                contactDetails: editModal.vendor.contactDetails,
                notes: editModal.vendor.notes
            }

            const response = await makeRequest2(
                `${updateVasVendor}/${facilityId}/${editModal.vendor._id}`,
                'POST',
                vendorData
            )

            if (response.success) {
                toastify('Vendor updated successfully', 'success')
                closeEditModal()
                fetchServiceVendors() // Refresh the list
            } else {
                throw new Error(response.error || 'Failed to update vendor')
            }
        } catch (error) {
            toastify(error.message, 'error')
        } finally {
            setEditModal(prev => ({ ...prev, loading: false }))
        }
    }

    const handleEditInputChange = (e) => {
        const { name, value } = e.target
        setEditModal(prev => ({
            ...prev,
            vendor: {
                ...prev.vendor,
                [name]: value
            }
        }))
    }

    const handleEditContactChange = (e) => {
        const { name, value } = e.target
        setEditModal(prev => ({
            ...prev,
            vendor: {
                ...prev.vendor,
                contactDetails: {
                    ...prev.vendor.contactDetails,
                    [name]: value
                }
            }
        }))
    }

    const handleEditOfferChange = (index, field, value) => {
        setEditModal(prev => {
            const updatedOffers = [...prev.vendor.offers]
            updatedOffers[index] = {
                ...updatedOffers[index],
                [field]: value
            }
            return {
                ...prev,
                vendor: {
                    ...prev.vendor,
                    offers: updatedOffers
                }
            }
        })
    }

    const handleEditAddOffer = () => {
        setEditModal(prev => ({
            ...prev,
            vendor: {
                ...prev.vendor,
                offers: [...prev.vendor.offers, { serviceId: '' }]
            }
        }))
    }

    const handleEditRemoveOffer = (index) => {
        setEditModal(prev => ({
            ...prev,
            vendor: {
                ...prev.vendor,
                offers: prev.vendor.offers.filter((_, i) => i !== index)
            }
        }))
    }

    // Get service name by ID
    const getServiceName = (serviceId) => {
        const service = services.find(s => s._id === serviceId)
        return service ? service.serviceName : 'Unknown Service'
    }

    useEffect(() => {
        if (activeTab === 'view-vendors') {
            fetchServiceVendors()
        } else if (activeTab === 'add-vendor') {
            fetchServices()
        }
    }, [activeTab])

    return (
        <Layout>
            <div>
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-body py-0">
                                <ul className="nav nav-tabs profile-tabs" id="myTab" role="tablist">
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link cursor-pointer ${activeTab === 'add-vendor' ? 'active' : ''}`}
                                            onClick={() => handleTabClick('add-vendor')}
                                            role="tab"
                                            aria-selected={activeTab === 'add-vendor'}
                                        >
                                            <i className="ti ti-user-plus me-2"></i>Add Vendor
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link cursor-pointer ${activeTab === 'view-vendors' ? 'active' : ''}`}
                                            onClick={() => handleTabClick('view-vendors')}
                                            role="tab"
                                            aria-selected={activeTab === 'view-vendors'}
                                        >
                                            <i className="ti ti-users me-2"></i>View Vendors
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="tab-content">
                            {/* Add Vendor Tab */}
                            {activeTab === 'add-vendor' && (
                                <div className="tab-pane show active" role="tabpanel">
                                    <div className="card">
                                        <div className="card-header">
                                            <h5>Add VAS Vendor</h5>
                                        </div>
                                        <div className='card-body'>
                                            <div className="row">
                                                <div className="col-lg-12 col-xxl-12">
                                                    <div className="card">
                                                        <div className="card-body position-relative">
                                                            <form onSubmit={handleSubmit}>
                                                                {/* Basic Information */}
                                                                <h6 className="fw-bold mb-3 text-primary">Basic Information</h6>

                                                                <div className="row">
                                                                    <div className="col-md-6 mb-3">
                                                                        <label className="form-label fw-semibold">
                                                                            Vendor Name <span className="text-danger">*</span>
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            name="name"
                                                                            value={name}
                                                                            onChange={handleChange}
                                                                            placeholder="Enter vendor name"
                                                                            disabled={loading}
                                                                            required
                                                                        />
                                                                    </div>

                                                                    <div className="col-md-6 mb-3">
                                                                        <label className="form-label fw-semibold">Address</label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            name="address"
                                                                            value={address}
                                                                            onChange={handleChange}
                                                                            placeholder="Enter complete address"
                                                                            rows="2"
                                                                            disabled={loading}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <hr />

                                                                {/* Contact Details */}
                                                                <h6 className="fw-bold mb-3 text-primary">Contact Details</h6>

                                                                <div className="row">
                                                                    <div className="col-md-4 mb-3">
                                                                        <label className="form-label fw-semibold">
                                                                            Contact Person <span className="text-danger">*</span>
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            name="contactName"
                                                                            value={contactName}
                                                                            onChange={handleChange}
                                                                            placeholder="Full name"
                                                                            disabled={loading}
                                                                            required
                                                                        />
                                                                    </div>

                                                                    <div className="col-md-4 mb-3">
                                                                        <label className="form-label fw-semibold">
                                                                            Phone Number <span className="text-danger">*</span>
                                                                        </label>
                                                                        <input
                                                                            type="tel"
                                                                            className="form-control"
                                                                            name="contactPhone"
                                                                            value={contactPhone}
                                                                            onChange={handleChange}
                                                                            placeholder="Enter phone number"
                                                                            disabled={loading}
                                                                            required
                                                                        />
                                                                    </div>

                                                                    <div className="col-md-4 mb-3">
                                                                        <label className="form-label fw-semibold">Email Address</label>
                                                                        <input
                                                                            type="email"
                                                                            className="form-control"
                                                                            name="contactEmail"
                                                                            value={contactEmail}
                                                                            onChange={handleChange}
                                                                            placeholder="Enter email address"
                                                                            disabled={loading}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <hr />

                                                                {/* Services Offered */}
                                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                                    <h6 className="fw-bold text-primary mb-0">Services Offered</h6>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={handleAddOffer}
                                                                        disabled={loading || fetchingServices}
                                                                    >
                                                                        <i className="fas fa-plus me-2"></i>
                                                                        Add Another Service
                                                                    </button>
                                                                </div>

                                                                {fetchingServices ? (
                                                                    <div className="text-center py-3">
                                                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                                            <span className="visually-hidden">Loading services...</span>
                                                                        </div>
                                                                        <span className="ms-2">Loading services...</span>
                                                                    </div>
                                                                ) : services.length === 0 ? (
                                                                    <div className="alert alert-warning">
                                                                        No services found. Please add services first.
                                                                    </div>
                                                                ) : (
                                                                    offers.map((offer, index) => (
                                                                        <div key={index} className="row mb-3 align-items-end">
                                                                            <div className="col-md-5">
                                                                                <label className="form-label fw-semibold">
                                                                                    {index === 0 ? 'Service' : ''}
                                                                                </label>
                                                                                <select
                                                                                    className="form-select"
                                                                                    value={offer.serviceId}
                                                                                    onChange={(e) => handleOfferChange(index, 'serviceId', e.target.value)}
                                                                                    disabled={loading}
                                                                                    required={index === 0}
                                                                                >
                                                                                    <option value="">Select Service</option>
                                                                                    {services.map(service => (
                                                                                        <option key={service._id} value={service._id}>
                                                                                            {service.serviceName}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>
                                                                            </div>

                                                                            <div className="col-md-2">
                                                                                {offers.length > 1 && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-outline-danger"
                                                                                        onClick={() => handleRemoveOffer(index)}
                                                                                        disabled={loading}
                                                                                    >
                                                                                        <i className="fas fa-trash"></i>
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                )}

                                                                <hr />

                                                                {/* Notes */}
                                                                <div className="row mb-3">
                                                                    <div className="col-md-12">
                                                                        <label className="form-label fw-semibold">Notes (Optional)</label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            name="notes"
                                                                            value={notes}
                                                                            onChange={handleChange}
                                                                            placeholder="Any additional notes about the vendor"
                                                                            rows="2"
                                                                            disabled={loading}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <hr />

                                                                {/* Actions */}
                                                                <div className="d-flex justify-content-end">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-light me-2"
                                                                        onClick={() => navigate(-1)}
                                                                        disabled={loading}
                                                                    >
                                                                        Cancel
                                                                    </button>

                                                                    <button
                                                                        type="submit"
                                                                        className="btn btn-primary px-4"
                                                                        disabled={loading}
                                                                    >
                                                                        {loading ? (
                                                                            <>
                                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                                Saving...
                                                                            </>
                                                                        ) : (
                                                                            'Save Vendor'
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* View Vendors Tab */}
                            {activeTab === 'view-vendors' && (
                                <div className="tab-pane show active" role="tabpanel">
                                    <div className="card">
                                        <div className="card-header">
                                            <h5>VAS Vendors</h5>
                                        </div>
                                        <div className='card-body'>
                                            {fetchingVendors ? (
                                                <div className="text-center py-5">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p className="mt-2">Loading vendors...</p>
                                                </div>
                                            ) : serviceVendors.length === 0 ? (
                                                <div className="text-center py-5">
                                                    <i className="ti ti-users" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                                    <h6 className="mt-3">No vendors found</h6>
                                                    <p className="text-muted">Get started by adding your first vendor</p>
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => setActiveTab('add-vendor')}
                                                    >
                                                        <i className="ti ti-user-plus me-2"></i>
                                                        Add Vendor
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-hover">
                                                        <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Vendor Name</th>
                                                                <th>Contact Person</th>
                                                                <th>Phone</th>
                                                                <th>Email</th>
                                                                <th>Services Offered</th>
                                                                <th>Status</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {serviceVendors.map((vendor, index) => (
                                                                <tr key={vendor._id}>
                                                                    <td>{index + 1}</td>
                                                                    <td>
                                                                        <strong>{vendor.name}</strong>
                                                                        {vendor.address && (
                                                                            <small className="d-block text-muted">
                                                                                <i className="ti ti-map-pin me-1"></i>
                                                                                {vendor.address.substring(0, 30)}...
                                                                            </small>
                                                                        )}
                                                                    </td>
                                                                    <td>{vendor.contactDetails?.name}</td>
                                                                    <td>{vendor.contactDetails?.phone}</td>
                                                                    <td>{vendor.contactDetails?.email || '-'}</td>
                                                                    <td>
                                                                        {vendor.offers && vendor.offers.length > 0 ? (
                                                                            <div>
                                                                                {vendor.offers.map((offer, idx) => (
                                                                                    <div key={idx} className="mb-1">
                                                                                        <span className="badge bg-info me-1">
                                                                                            {offer.serviceId?.serviceName || getServiceName(offer.serviceId)}
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-muted">No services</span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <span className={`badge ${vendor.status === 'ACTIVE' ? 'bg-success' : 'bg-danger'}`}>
                                                                            {vendor.status}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-primary me-2"
                                                                            title="View Details"
                                                                            onClick={() => handleViewVendor(vendor)}
                                                                        >
                                                                            <i className="ti ti-eye"></i>
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-warning"
                                                                            title="Edit"
                                                                            onClick={() => handleEditVendor(vendor)}
                                                                        >
                                                                            <i className="ti ti-edit"></i>
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* View Vendor Modal */}
            {viewModal.show && viewModal.vendor && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} id="viewVendorModal" tabIndex="-1" role="dialog" aria-modal="true">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Vendor Details</h5>
                                <button type="button" className="btn-close" onClick={closeViewModal} aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="fw-semibold">Vendor Name:</label>
                                        <p className="mb-0">{viewModal.vendor.name}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="fw-semibold">Status:</label>
                                        <p className="mb-0">
                                            <span className={`badge ${viewModal.vendor.status === 'ACTIVE' ? 'bg-success' : 'bg-danger'}`}>
                                                {viewModal.vendor.status}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                {viewModal.vendor.address && (
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <label className="fw-semibold">Address:</label>
                                            <p className="mb-0">{viewModal.vendor.address}</p>
                                        </div>
                                    </div>
                                )}

                                <h6 className="fw-bold text-primary mt-3 mb-3">Contact Details</h6>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="fw-semibold">Contact Person:</label>
                                        <p className="mb-0">{viewModal.vendor.contactDetails?.name}</p>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="fw-semibold">Phone:</label>
                                        <p className="mb-0">{viewModal.vendor.contactDetails?.phone}</p>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="fw-semibold">Email:</label>
                                        <p className="mb-0">{viewModal.vendor.contactDetails?.email || '-'}</p>
                                    </div>
                                </div>

                                <h6 className="fw-bold text-primary mt-3 mb-3">Services Offered</h6>
                                {viewModal.vendor.offers && viewModal.vendor.offers.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Service</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {viewModal.vendor.offers.map((offer, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{offer.serviceId?.serviceName || getServiceName(offer.serviceId)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-muted">No services offered</p>
                                )}

                                {viewModal.vendor.notes && (
                                    <div className="row mt-3">
                                        <div className="col-12">
                                            <label className="fw-semibold">Notes:</label>
                                            <p className="mb-0">{viewModal.vendor.notes}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeViewModal}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Vendor Modal */}
            {editModal.show && editModal.vendor && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} id="editVendorModal" tabIndex="-1" role="dialog" aria-modal="true">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Vendor</h5>
                                <button type="button" className="btn-close" onClick={closeEditModal} aria-label="Close"></button>
                            </div>
                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <h6 className="fw-bold text-primary mb-3">Basic Information</h6>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Vendor Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="name"
                                                value={editModal.vendor.name}
                                                onChange={handleEditInputChange}
                                                required
                                                disabled={editModal.loading}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Address</label>
                                            <textarea
                                                className="form-control"
                                                name="address"
                                                value={editModal.vendor.address || ''}
                                                onChange={handleEditInputChange}
                                                rows="2"
                                                disabled={editModal.loading}
                                            />
                                        </div>
                                    </div>

                                    <hr />

                                    <h6 className="fw-bold text-primary mb-3">Contact Details</h6>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label fw-semibold">Contact Person *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="name"
                                                value={editModal.vendor.contactDetails?.name || ''}
                                                onChange={handleEditContactChange}
                                                required
                                                disabled={editModal.loading}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label fw-semibold">Phone Number *</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                name="phone"
                                                value={editModal.vendor.contactDetails?.phone || ''}
                                                onChange={handleEditContactChange}
                                                required
                                                disabled={editModal.loading}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label fw-semibold">Email Address</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email"
                                                value={editModal.vendor.contactDetails?.email || ''}
                                                onChange={handleEditContactChange}
                                                disabled={editModal.loading}
                                            />
                                        </div>
                                    </div>

                                    <hr />

                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-bold text-primary mb-0">Services Offered</h6>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={handleEditAddOffer}
                                            disabled={editModal.loading}
                                        >
                                            <i className="fas fa-plus me-2"></i>
                                            Add Another Service
                                        </button>
                                    </div>

                                    {editModal.vendor.offers && editModal.vendor.offers.length > 0 ? (
                                        editModal.vendor.offers.map((offer, index) => (
                                            <div key={index} className="row mb-3 align-items-end">
                                                <div className="col-md-5">
                                                    <label className="form-label fw-semibold">
                                                        {index === 0 ? 'Service' : ''}
                                                    </label>
                                                    <select
                                                        className="form-select"
                                                        value={offer.serviceId?._id || offer.serviceId || ''}
                                                        onChange={(e) => handleEditOfferChange(index, 'serviceId', e.target.value)}
                                                        disabled={editModal.loading}
                                                        required={index === 0}
                                                    >
                                                        <option value="">Select Service</option>
                                                        {services.map(service => (
                                                            <option key={service._id} value={service._id}>
                                                                {service.serviceName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="col-md-2">
                                                    {editModal.vendor.offers.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger"
                                                            onClick={() => handleEditRemoveOffer(index)}
                                                            disabled={editModal.loading}
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="alert alert-warning">
                                            At least one service offer is required.
                                        </div>
                                    )}

                                    <hr />

                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            <label className="form-label fw-semibold">Notes (Optional)</label>
                                            <textarea
                                                className="form-control"
                                                name="notes"
                                                value={editModal.vendor.notes || ''}
                                                onChange={handleEditInputChange}
                                                placeholder="Any additional notes about the vendor"
                                                rows="2"
                                                disabled={editModal.loading}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-light" onClick={closeEditModal} disabled={editModal.loading}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={editModal.loading}>
                                        {editModal.loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            'Update Vendor'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    )
}

export default VASvendors